/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app
 * 
 * SSO Support: Checks for sso_token URL parameter to enable 
 * seamless authentication from the main valpha.io frontend
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User } from '../services/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Check for SSO token in URL and store it
 * This enables seamless login from the main valpha.io frontend
 */
const handleSSOToken = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const ssoToken = urlParams.get('sso_token');
  
  if (ssoToken) {
    // Store the token
    localStorage.setItem('token', ssoToken);
    
    // Clean up URL by removing the token parameter
    urlParams.delete('sso_token');
    const newUrl = urlParams.toString() 
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
    
    return ssoToken;
  }
  
  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      // First check for SSO token in URL
      handleSSOToken();
      
      const storedUser = authService.getUser();
      const token = authService.getAccessToken();

      if (token) {
        // Try to fetch fresh user data, but don't nuke the session on failure.
        // The main frontend login may not populate the 'user' key, so we also
        // derive a minimal user from the JWT as a last-resort fallback.
        try {
          const freshUser = await authService.fetchUserProfile();
          setUser(freshUser);
        } catch (error) {
          console.warn('Could not fetch user profile, using fallback:', error);
          // Fallback 1: stored user from localStorage
          if (storedUser) {
            setUser(storedUser);
          } else {
            // Fallback 2: derive minimal user from JWT payload
            const decoded = authService.decodeToken(token);
            if (decoded) {
              const fallbackUser: User = {
                id: decoded.user_id ?? 0,
                username: decoded.username ?? decoded.email ?? 'user',
                email: decoded.email ?? '',
              };
              setUser(fallbackUser);
            } else {
              // Token is completely invalid / garbage
              authService.logout();
              setUser(null);
            }
          }
        }
      } else if (storedUser) {
        // Have stored user but no token - clear invalid state
        authService.logout();
        setUser(null);
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string) => {
    const loggedInUser = await authService.login({ username, password });
    console.log('User logged in:', loggedInUser);
    setUser(loggedInUser);
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const freshUser = await authService.fetchUserProfile();
      setUser(freshUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
